import type { ComponentProps, ReactNode } from "react";
import { ClickableChip, DefaultChip, SelectableChip } from "./Core";
import type { Checkbox } from "@ark-ui/react";
import { chipVariants } from "./variants";

type BaseChipProps = {
  prefixIcon?: ReactNode;
  suffixIcon?: ReactNode;
  size?: "xs" | "sm" | "md";
  variant?: "fill" | "outline";
  radius?: "square" | "round";
  color?: "default" | "primary" | "error" | "warn" | "info" | "success";
};

type ClickableChipProps = React.ButtonHTMLAttributes<HTMLButtonElement> & BaseChipProps;

type SelectableChipProps = Omit<ComponentProps<typeof Checkbox.Root>, "checked" | "onCheckedChange"> &
  BaseChipProps & {
    selected?: boolean;
    onSelect?: (selected: boolean) => void;
  };

type DefaultChipProps = React.HTMLAttributes<HTMLSpanElement> & BaseChipProps;

type ChipProps =
  | ({ mode: "click" } & ClickableChipProps)
  | ({ mode: "select" } & SelectableChipProps)
  | ({ mode?: "default" } & DefaultChipProps);

export const Chip = (props: ChipProps) => {
  if (props.mode === "click") {
    const {
      prefixIcon: PrefixIcon,
      suffixIcon: SuffixIcon,
      size,
      variant,
      radius,
      color,
      disabled = false,
      mode,
      children,
      ...rest
    } = props;

    return (
      <ClickableChip
        className={chipVariants({
          size,
          variant,
          radius,
          disabled,
          color,
          mode,
          hasPrefixIcon: !!PrefixIcon,
          hasSuffixIcon: !!SuffixIcon,
        })}
        disabled={disabled}
        {...rest}
      >
        {PrefixIcon && <span className="flex-shrink-0">{PrefixIcon}</span>}
        {children}
        {SuffixIcon && <span className="flex-shrink-0">{SuffixIcon}</span>}
      </ClickableChip>
    );
  }

  if (props.mode === "select") {
    const {
      prefixIcon: PrefixIcon,
      suffixIcon: SuffixIcon,
      size,
      variant,
      radius,
      color,
      disabled = false,
      mode,
      children,
      ...rest
    } = props;

    return (
      <SelectableChip
        className={chipVariants({
          size,
          variant,
          radius,
          disabled,
          color,
          mode,
          hasPrefixIcon: !!PrefixIcon,
          hasSuffixIcon: !!SuffixIcon,
        })}
        disabled={disabled}
        {...rest}
      >
        {PrefixIcon && <span className="flex-shrink-0">{PrefixIcon}</span>}
        {children}
        {SuffixIcon && <span className="flex-shrink-0">{SuffixIcon}</span>}
      </SelectableChip>
    );
  }

  const {
    prefixIcon: PrefixIcon,
    suffixIcon: SuffixIcon,
    size,
    variant,
    radius,
    color,
    mode,
    children,
    ...rest
  } = props;

  return (
    <DefaultChip
      className={chipVariants({
        size,
        variant,
        radius,
        color,
        mode,
        hasPrefixIcon: !!PrefixIcon,
        hasSuffixIcon: !!SuffixIcon,
      })}
      {...rest}
    >
      {PrefixIcon && <span className="flex-shrink-0">{PrefixIcon}</span>}
      {children}
      {SuffixIcon && <span className="flex-shrink-0">{SuffixIcon}</span>}
    </DefaultChip>
  );
};
